import React, { forwardRef, Ref, useRef, useState, ReactNode, useMemo } from 'react';
import { useMergeRefs } from '@floating-ui/react';
import classNames from 'classnames';
import { DropdownLabel, DropdownLabelProps } from './DropdownLabel';
import { getZIndex, CSSValueWithLength } from '../../styles';
import { useProviderConfig } from '../provider';
import { InputSize } from '../input/types';
import { AriaFocusProps } from '../../types/common';
import { DropdownOptions } from './DropdownOptions';
import { DEFAULT_DROPDOWN_MAX_HEIGHT } from './constants';
import { useDropdownFloating, useDropdownInteractions } from './hooks';
import { DropdownOption } from './types';

export interface DropdownProps<OptionValue> {
  className?: string;
  width?: CSSValueWithLength;
  /** @default DEFAULT_DROPDOWN_MAX_HEIGHT */
  maxHeight?: number;
  /** @default getZIndex('dropdown') */
  zIndex?: number;
  /** @default 'medium' */
  size?: InputSize;
  labelHeight?: CSSValueWithLength;
  optionItemHeight?: number;
  disabled?: boolean;
  /** @default true */
  optionsMatchRefWidth?: boolean;
  options: Array<DropdownOption<OptionValue>>;
  placeholder?: string;
  status?: DropdownLabelProps['status'];
  value?: OptionValue;
  /**
   * floating 요소가 reference 영역을 넘어갈 경우 자동 위치 처리 사용 유무
   *  - 자동 위치 상/하만 적용됩니다.
   *
   * @default true
   */
  autoPlacement?: boolean;
  keyExtractor?: (item: DropdownOption<OptionValue>, index: number) => string | number;
  onChange?: (value: OptionValue) => void;
  renderLabel?: (selectedOption: DropdownOption<OptionValue>) => ReactNode;
  enableVirtualScroll?: boolean;
}

function DropdownInner<OptionValue extends string | number>(
  {
    className,
    width,
    maxHeight = DEFAULT_DROPDOWN_MAX_HEIGHT,
    zIndex = getZIndex('dropdown'),
    size: sizeProp = 'medium',
    labelHeight,
    optionItemHeight,
    disabled,
    optionsMatchRefWidth = true,
    options,
    placeholder,
    status,
    value: valueProp,
    autoPlacement: autoPlacementProps = true,
    unstableFocusVisible,
    keyExtractor,
    onChange,
    renderLabel,
    enableVirtualScroll,
  }: DropdownProps<OptionValue> & AriaFocusProps,
  ref: Ref<HTMLDivElement>,
) {
  const { locale } = useProviderConfig();
  const [opened, setOpened] = useState(false);
  const [focusIndex, setFocusIndex] = useState<number | null>(null);
  const selectedIndex = options.findIndex(({ value }) => value === valueProp);
  const selectedOption = options[selectedIndex];

  const listElements = useMemo<Array<HTMLElement | null>>(
    () => Array.from({ length: options.length }).fill(null) as null[],
    [options],
  );
  const optionListRef = useRef<Array<HTMLElement | null>>(listElements);

  const typeaheadList = useMemo(() => options.map(({ label }) => label?.toString() ?? null), [options]);
  const typeaheadListRef = useRef<Array<string | null>>(typeaheadList);

  const { refs, floatingStyles, context, isPositioned } = useDropdownFloating({
    open: opened,
    onOpenChange: setOpened,
    optionsMatchRefWidth,
    maxHeight,
    autoPlacement: autoPlacementProps,
  });
  const referenceRef = useMergeRefs([refs.setReference, ref]);

  const { getReferenceProps, getFloatingProps, getItemProps } = useDropdownInteractions({
    context,
    useClickProps: {
      enabled: !disabled,
    },
    useListNavigationProps: {
      listRef: optionListRef,
      activeIndex: focusIndex,
      selectedIndex,
      onNavigate: setFocusIndex,
      disabledIndices: [],
      virtual: true,
    },
    useTypeaheadProps: {
      listRef: typeaheadListRef,
      activeIndex: focusIndex,
      onMatch: setFocusIndex,
    },
  });

  const labelRendered = useMemo(() => {
    if (selectedOption === undefined) return placeholder;
    if (typeof renderLabel === 'function') return renderLabel(selectedOption);

    return selectedOption?.label;
  }, [selectedOption, renderLabel]);

  return (
    <>
      <DropdownLabel
        {...getReferenceProps({
          'ref': referenceRef,
          'className': classNames('pds-dropdown-label', className),
          'aria-label': placeholder ?? locale?.Dropdown.placeholder,
          width,
          disabled,
          'selected': selectedIndex !== -1,
          'tabIndex': 0,
        })}
        height={labelHeight}
        expanded={opened}
        status={status}
        size={sizeProp}
      >
        {labelRendered}
      </DropdownLabel>
      <DropdownOptions
        refs={refs}
        size={sizeProp}
        optionItemHeight={optionItemHeight}
        setRefs={(node, index) => {
          optionListRef.current[index] = node;
        }}
        floatingStyles={floatingStyles}
        getFloatingProps={getFloatingProps}
        getItemProps={getItemProps}
        onClose={() => {
          setOpened(false);
          setFocusIndex(null);
        }}
        value={valueProp}
        zIndex={zIndex}
        unstableFocusVisible={unstableFocusVisible}
        keyExtractor={keyExtractor}
        onChange={onChange}
        context={context}
        focusIndex={focusIndex}
        options={options}
        isPositioned={isPositioned}
        setFocusIndex={setFocusIndex}
        enableVirtualScroll={enableVirtualScroll}
      />
    </>
  );
}

export const Dropdown = forwardRef(DropdownInner) as (<OptionValue extends string | number>(
  props: DropdownProps<OptionValue> & AriaFocusProps & { ref?: Ref<HTMLDivElement> },
) => JSX.Element) & { displayName?: string };

Dropdown.displayName = 'Dropdown';
